import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  MAINTENANCE_ENABLED,
  MAINTENANCE_END,
  MAINTENANCE_START,
} from '@config/config';
import { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { CloseCircle, Warning2 } from 'iconsax-react';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const isIsoUtcWithZ = (value: string) => /Z$/i.test(value);

const formatDate = (value: string) => {
  if (!value) return '';
  // Expect ISO 8601 with trailing 'Z' (UTC). Parse as UTC and convert to PT.
  const parsed = dayjs.utc(value);
  return parsed.isValid()
    ? parsed.tz('America/Los_Angeles').format('MMM D, YYYY h:mm A')
    : value;
};

const MaintenanceBanner = () => {
  const [isOpen, setIsOpen] = useState(true);
  if (!MAINTENANCE_ENABLED) return null;

  // Runtime validation of env timestamps
  useEffect(() => {
    if (!MAINTENANCE_START || !MAINTENANCE_END) {
      console.warn(
        'MaintenanceBanner: VITE_MAINTENANCE_START and VITE_MAINTENANCE_END should be set when VITE_MAINTENANCE_ENABLED=true',
      );
      return;
    }

    if (!isIsoUtcWithZ(MAINTENANCE_START)) {
      console.warn(
        "MaintenanceBanner: VITE_MAINTENANCE_START must be ISO 8601 UTC with trailing 'Z' (e.g., 2025-09-20T02:00:00Z)",
      );
    }
    if (!isIsoUtcWithZ(MAINTENANCE_END)) {
      console.warn(
        "MaintenanceBanner: VITE_MAINTENANCE_END must be ISO 8601 UTC with trailing 'Z' (e.g., 2025-09-20T04:00:00Z)",
      );
    }

    const startUtc = dayjs.utc(MAINTENANCE_START);
    const endUtc = dayjs.utc(MAINTENANCE_END);

    if (!startUtc.isValid()) {
      console.warn(
        'MaintenanceBanner: VITE_MAINTENANCE_START is not a valid ISO 8601 UTC timestamp',
      );
    }
    if (!endUtc.isValid()) {
      console.warn(
        'MaintenanceBanner: VITE_MAINTENANCE_END is not a valid ISO 8601 UTC timestamp',
      );
    }
    if (startUtc.isValid() && endUtc.isValid() && endUtc.isBefore(startUtc)) {
      console.warn(
        'MaintenanceBanner: VITE_MAINTENANCE_END is before VITE_MAINTENANCE_START',
      );
    }
  }, [MAINTENANCE_ENABLED, MAINTENANCE_START, MAINTENANCE_END]);

  const start = formatDate(MAINTENANCE_START);
  const end = formatDate(MAINTENANCE_END);
  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <div className={styles.MaintenanceBanner} style={{ display: isOpen ? 'block' : 'none' }}>
      <div className={styles.MaintenanceBannerContent}>
        <div className="flex items-center justify-center">
          <Warning2 size="48"
            color="var(--color-danger)"
            variant="Bulk" />
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-sm font-bold">
            Scheduled Maintenance Notice
          </div>

          <div className="text-sm">
            Leo will be unavailable between{' '}
            <span className="font-bold">
              {start}
            </span>{' '}
            and{' '}
            <span className="font-bold">
              {end}
            </span>{' '}
            Pacific Time (US). Please avoid using the platform during this
            window. Our development team is working hard to deliver new
            features and improvements.
          </div>
        </div>
        <div className="flex items-center justify-center">
          <button className={styles.CloseButton} onClick={handleClose} aria-label="Close maintenance banner">
            <CloseCircle size="24" variant="Bulk" color="red" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceBanner;


